import type { VisitSort, VisitStatus } from '@itvisits/shared';
import { VISIT_STATUSES } from '@itvisits/shared';
import { Button, Input, Select, type SelectOption } from '../../ui';

export interface VisitSearch {
  status: VisitStatus[];
  technicianId?: number | undefined;
  facilityId?: number | undefined;
  clusterId?: number | undefined;
  from?: string | undefined;
  to?: string | undefined;
  wip: boolean;
  q: string;
  sort: VisitSort;
  page: number;
  open?: number | undefined;
}

export const DEFAULT_SEARCH: VisitSearch = {
  status: [],
  wip: true,
  q: '',
  sort: 'scheduled_desc',
  page: 1,
};

export interface VisitFiltersProps {
  value: VisitSearch;
  onChange: (next: VisitSearch) => void;
  technicians: SelectOption[];
  facilities: SelectOption[];
  clusters: SelectOption[];
}

const STATUS_LABEL: Record<VisitStatus, string> = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  completed: 'Completed',
  validated: 'Validated',
  cancelled: 'Cancelled',
};

/** Filter bar; every change resets to page 1. State lives in the URL (constitution §5.6). */
export function VisitFilters(props: VisitFiltersProps): React.JSX.Element {
  const v = props.value;
  const set = (patch: Partial<VisitSearch>): void => props.onChange({ ...v, ...patch, page: 1 });
  const num = (s: string): number | undefined => (s === '' ? undefined : Number(s));
  const isDefault = JSON.stringify({ ...v, page: 1 }) === JSON.stringify(DEFAULT_SEARCH);
  return (
    <div className="filters" role="search" aria-label="Visit filters">
      <label className="filters__item">
        <span className="field__label">Cluster</span>
        <Select
          value={v.clusterId ?? ''}
          onChange={(e) =>
            // A facility or technician outside the new cluster would silently match nothing.
            set({ clusterId: num(e.target.value), facilityId: undefined, technicianId: undefined })
          }
          options={props.clusters}
          placeholder="All clusters"
        />
      </label>
      <label className="filters__item">
        <span className="field__label">Facility</span>
        <Select
          value={v.facilityId ?? ''}
          onChange={(e) => set({ facilityId: num(e.target.value) })}
          options={props.facilities}
          placeholder="All facilities"
        />
      </label>
      <label className="filters__item">
        <span className="field__label">Status</span>
        <Select
          value={v.status[0] ?? ''}
          onChange={(e) =>
            set({
              status: e.target.value ? [e.target.value as VisitStatus] : [],
              wip: e.target.value ? false : v.wip,
            })
          }
          options={VISIT_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          placeholder="All statuses"
        />
      </label>
      <label className="filters__item">
        <span className="field__label">Technician</span>
        <Select
          value={v.technicianId ?? ''}
          onChange={(e) => set({ technicianId: num(e.target.value) })}
          options={props.technicians}
          placeholder="All technicians"
        />
      </label>
      <label className="filters__item">
        <span className="field__label">From</span>
        <Input
          type="date"
          value={v.from ?? ''}
          onChange={(e) => set({ from: e.target.value || undefined })}
        />
      </label>
      <label className="filters__item">
        <span className="field__label">To</span>
        <Input
          type="date"
          value={v.to ?? ''}
          onChange={(e) => set({ to: e.target.value || undefined })}
        />
      </label>
      <label className="filters__item filters__item--grow">
        <span className="field__label">Search</span>
        <Input
          type="search"
          value={v.q}
          placeholder="Scope, facility or technician…"
          onChange={(e) => set({ q: e.target.value })}
        />
      </label>
      <div className="filters__item">
        <span className="field__label">View</span>
        <div className="segmented" role="group" aria-label="View">
          <Button
            size="sm"
            variant={v.wip ? 'ghost' : 'primary'}
            aria-pressed={!v.wip}
            onClick={() => set({ wip: false })}
          >
            All
          </Button>
          <Button
            size="sm"
            variant={v.wip ? 'primary' : 'ghost'}
            aria-pressed={v.wip}
            onClick={() => set({ wip: true, status: [] })}
          >
            WIP
          </Button>
        </div>
      </div>
      <div className="filters__item">
        <span className="field__label">Sort</span>
        <div className="segmented" role="group" aria-label="Sort">
          <Button
            size="sm"
            variant={v.sort.startsWith('scheduled') ? 'primary' : 'ghost'}
            onClick={() =>
              set({ sort: v.sort === 'scheduled_asc' ? 'scheduled_desc' : 'scheduled_asc' })
            }
          >
            Date {v.sort === 'scheduled_desc' ? '↓' : '↑'}
          </Button>
          <Button
            size="sm"
            variant={v.sort === 'status' ? 'primary' : 'ghost'}
            onClick={() => set({ sort: 'status' })}
          >
            Status
          </Button>
        </div>
      </div>
      <div className="filters__item filters__item--end">
        <Button size="sm" disabled={isDefault} onClick={() => props.onChange(DEFAULT_SEARCH)}>
          Clear filters
        </Button>
      </div>
    </div>
  );
}
